import { useEffect } from "react";
import type { ID, Region, SourceConfig } from "@gooner/shared";
import { syncLeaderOf } from "@gooner/shared";
import type { CollectionOption } from "../control/lib/monitorTemplates";
import { FeedPicker } from "../control/components/FeedPicker";
import { IconClose } from "../control/ui/icons";

/**
 * The wall's region feed popup: click a region, change what it plays without leaving
 * the glass. STRICTLY the collections and keyword sections — the same FeedPicker the
 * editor's palette mounts, so the two surfaces cannot drift — and nothing else; names,
 * sync, auto-optimise and layout stay behind the bar's pencil in the full editor.
 *
 * Mounted at the DisplayApp's top level beside the other sheets, never inside the
 * tile: portal or not, React events bubble through the REACT tree, so a dialog
 * rendered as the tile's child would deliver every click inside it back to the tile's
 * own onClick — the very handler that opens this.
 *
 * A follower's popup shows and edits its LEADER's feed — one configuration, two tiles
 * (the notice says so) — through the same resolution the editor's palette uses.
 */
export function RegionFeedDialog({
  region,
  regions,
  index,
  collections,
  tags,
  loading,
  onSetSource,
  onClose,
}: {
  /** The clicked region (possibly a follower). */
  region: Region;
  /** The authored region list, for leader resolution and the notice's naming. */
  regions: Region[];
  /** Position in the authored list, for the "Region N" fallback name. */
  index: number;
  collections: CollectionOption[];
  tags: string[];
  loading: boolean;
  /** A pick, already routed: the id is the LEADER the edit must land on. */
  onSetSource: (leaderId: ID, source: SourceConfig) => void;
  onClose: () => void;
}) {
  const leader = syncLeaderOf(region, regions);
  const shared = leader ?? region;
  const name = (r: Region, i: number) => r.name?.trim() || `Region ${i + 1}`;

  // Escape closes — the one keyboard affordance a modal owes. The wall's Space and
  // arrow listeners already stand down while this is open (feedEditRef).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      // Backdrop alone closes: e.target is the panel (or something in it) for any
      // click inside, so only a genuine outside click passes this guard.
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`What ${name(region, index)} plays`}
        className="flex max-h-[85vh] w-full max-w-md flex-col rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] shadow-2xl"
      >
        <div className="flex items-center justify-between gap-2 border-b border-[var(--color-edge)] px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-zinc-100">
              {name(region, index)}
            </div>
            {leader ? (
              // The one line that stops the popup lying: everything below is the
              // LEADER's config — one feed, two tiles, edited from either.
              <div className="truncate text-[11px] text-zinc-400">
                Synced with{" "}
                {name(shared, regions.findIndex((r) => r.id === shared.id))} — edits
                change both.
              </div>
            ) : (
              <div className="text-[11px] text-zinc-500">What this region plays.</div>
            )}
          </div>
          <button
            type="button"
            aria-label="Close"
            title="Close"
            onClick={onClose}
            className="rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-[var(--color-panel-2)] hover:text-zinc-100"
          >
            <IconClose width={16} height={16} />
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto px-4 py-4">
          {/* No onCreateCollection on purpose: building a collection is a detour the
              glass has no room for; the "+ New" chip stays an editor affordance. */}
          <FeedPicker
            collections={collections}
            tags={tags}
            loading={loading}
            source={shared.source}
            onSetSource={(s) => onSetSource(shared.id, s)}
          />
        </div>
      </div>
    </div>
  );
}
